import { i18n } from '@nutrien/cxp-components';
import dayjs, { Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Subscription } from 'rxjs';

import { useMst } from '../../mobx-models/Root';
import { BorerActivityDocument } from '../../rxdb/BorerActivity/queryBuilder';
import { useBorerActivity } from '../../rxdb/BorerActivity/useBorerActivity';
import { BorerActivityType } from '../../rxdb/BorerActivityType/queryBuilder';
import { useBorerActivityType } from '../../rxdb/BorerActivityType/useBorerActivityType';
import { useBorerShiftActivityEmployees } from '../../rxdb/BorerShiftActivityEmployees/useBorerShiftActivityEmployees';
import { DelayActivityTypeCategory } from '../../rxdb/DelayActivityTypeCategory/queryBuilder';
import { useDelayActivityTypeCategory } from '../../rxdb/DelayActivityTypeCategory/useDelayActivityTypeCategory';
import { FormField } from '../../rxdb/Employees/formHelper';
import { EmployeeDocument } from '../../rxdb/Employees/queryBuilder';
import { useEmployees } from '../../rxdb/Employees/useEmployees';
import { useDateFormatters, useNotification } from '../../utilities';
import { USER_TIMEZONE } from '../../utilities/hooks/useDateFormatters';
import { getShiftCorrectedTime } from '../../utilities/shiftTimeUtilities';
import { translate } from '../../utilities/siteLanguageTranslation/siteLanguageTranslation';
import { uniq } from '../../utilities/sortHelper';
import AddBorerActivityCard from '../AddBorerActivityCard/AddBorerActivityCard';
import GenericSidePanel from '../GenericSidePanel';
import { IAppointment } from '../pages/PiSightPage/DelaysSchedulerView/ActivitiesSchedulerHelpers';

dayjs.extend(timezone);

interface Props {
  open: boolean;
  onClose: (newDelayId?: string) => void;
  onOpen: () => void;
  onCancel?: () => void;
  prevAppointment?: IAppointment | null;
  readOnly: boolean;
}

const AddBorerActivitySidePanel = ({
  open,
  onClose,
  onOpen,
  onCancel,
  prevAppointment,
  readOnly,
}: Props) => {
  const { shiftPicker } = useMst();
  const { setBorerActivity } = useBorerActivity();
  const { allDelayTypeCategories } = useDelayActivityTypeCategory();
  const { sortedActivityTypes } = useBorerActivityType();
  const {
    borerShiftActivityEmployeesInitialized,
    borerShiftActivityEmployeeCollection,
    listBorerShiftActivityEmployees,
    setEmployeesOnBorerShiftActivity,
  } = useBorerShiftActivityEmployees();
  const { groupedEmployeesList: employees } = useEmployees({
    isActive: true,
    onlyConstructionAndProduction: false,
    populateCrew: true,
    populatedPosition: true,
    onlyActiveCrew: false,
  });

  const { stripSecondsAndMilliseconds } = useDateFormatters();
  const { successNotification, errorNotification } = useNotification();

  // BorerDelay state
  const [startTime, setStartTime] = useState<Dayjs>(stripSecondsAndMilliseconds(dayjs()));
  const [endTime, setEndTime] = useState<Dayjs>(
    stripSecondsAndMilliseconds(dayjs().add(30, 'minute')),
  );
  const [startErrorText, setStartErrorText] = useState<string>('');
  const [endErrorText, setEndErrorText] = useState<string>('');

  const emptyCompletedBy = [
    {
      value: undefined,
      initialValue: undefined,
      hasEdits: false,
      error: undefined,
    },
  ];

  const [category, setCategory] = useState<DelayActivityTypeCategory>();
  const [activityType, setActivityType] = useState<BorerActivityType>();
  const [comment, setComment] = useState<string>();

  // collections
  const [activityTypes, setActivityTypes] = useState<BorerActivityType[]>([]);
  const [activityEmployees, setActivityEmployees] = useState<EmployeeDocument[]>([]);
  const [completedByFields, setCompletedByFields] =
    useState<FormField<EmployeeDocument | undefined>[]>(emptyCompletedBy);

  // subscriptions
  const [activityEmployeesSubscription, setActivityEmployeesSubscription] =
    useState<Subscription>();

  // Side Panel Controls
  const [fieldsEdited, setFieldsEdited] = useState({});
  const [canSave, setCanSave] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [undo, setUndo] = useState(false);

  const hasEdits = useMemo(() => {
    return !Object.values(fieldsEdited).every(editedFields => !editedFields);
  }, [fieldsEdited]);

  useEffect(() => {
    return () => {
      if (activityEmployeesSubscription) {
        activityEmployeesSubscription.unsubscribe();
      }
    };
  }, []);

  useEffect(() => {
    if (open === true && undo === false) {
      setCanSave(false);
      setIsSaving(false);
      setActivityTypes([]);
      setFieldsEdited({});
      setStartTime(
        prevAppointment
          ? dayjs.tz(prevAppointment.startDate, USER_TIMEZONE)
          : stripSecondsAndMilliseconds(dayjs().tz()),
      );
      setEndTime(
        prevAppointment
          ? dayjs.tz(prevAppointment.endDate, USER_TIMEZONE)
          : stripSecondsAndMilliseconds(dayjs().tz().add(30, 'minute')),
      );
      setActivityType(undefined);
      setCategory(undefined);
      setComment(prevAppointment?.comment || '');

      if (!prevAppointment) {
        setCompletedByFields(emptyCompletedBy);
      }
    }
    if (open === true) {
      setUndo(false);
    }
  }, [open]);

  const getBorerActivityTypes = async () => {
    try {
      setActivityTypes(sortedActivityTypes);
      if (prevAppointment && open === true && undo === false) {
        const prevActivityType = sortedActivityTypes.find(
          x => x.id === prevAppointment.delayOrActivityTypeId,
        );
        setActivityType(prevActivityType);
        if (prevActivityType) {
          setCategory(
            allDelayTypeCategories.find(x => x.id === prevActivityType.delayActivityTypeCategory),
          );
        }
      }
    } catch (error) {
      console.log(
        '🚀 ~ file: AddBorerActivitySidePanel.tsx ~ line 179 ~ getBorerActivityTypes ~ error',
        error,
      );
    }
  };

  const getBorerShiftActivityEmployees = async () => {
    try {
      const result = await listBorerShiftActivityEmployees(prevAppointment?.id);
      setActivityEmployees(result);
      return result;
    } catch (error) {
      console.log(
        '🚀 ~ file: AddBorerActivitySidePanel.tsx ~ line 197 ~ getBorerShiftActivityEmployees ~ error',
        error,
      );
    }
  };

  useEffect(() => {
    getBorerActivityTypes();
  }, [open, sortedActivityTypes]);

  const setupActivityEmployees = async () => {
    const shiftActivityEmployees = await getBorerShiftActivityEmployees();
    if (!shiftActivityEmployees) return;

    const mapped = shiftActivityEmployees.map(x => {
      return {
        value: x,
        initialValue: x,
        hasEdits: false,
        error: undefined,
      };
    });

    setCompletedByFields(prevAppointment && mapped.length > 0 ? mapped : emptyCompletedBy);
  };

  useEffect(() => {
    if (
      !undo &&
      open &&
      borerShiftActivityEmployeesInitialized &&
      borerShiftActivityEmployeeCollection
    ) {
      setupActivityEmployees();
      const activityEmployeesSub = borerShiftActivityEmployeeCollection.$.subscribe(() => {
        getBorerShiftActivityEmployees();
      });
      setActivityEmployeesSubscription(activityEmployeesSub);
    }
  }, [open, borerShiftActivityEmployeesInitialized]);

  const onSetUndo = (value: boolean) => {
    setUndo(value);
  };

  const onStartTimeChanged = (date: Dayjs) => {
    const adjustedDate = getShiftCorrectedTime(date, shiftPicker.Date, shiftPicker.Type);
    setFieldsEdited(prev => {
      return {
        ...prev,
        ...{
          startTime:
            dayjs.tz(prevAppointment?.startDate, USER_TIMEZONE).toISOString() !==
            dayjs.tz(date, USER_TIMEZONE).toISOString(),
        },
      };
    });

    setStartTime(adjustedDate);
  };

  const onEndTimeChanged = (date: Dayjs) => {
    const adjustedDate = getShiftCorrectedTime(date, shiftPicker.Date, shiftPicker.Type);
    setFieldsEdited(prev => {
      return {
        ...prev,
        ...{
          endDate:
            dayjs.tz(prevAppointment?.endDate, USER_TIMEZONE).toISOString() !==
            dayjs.tz(date, USER_TIMEZONE).toISOString(),
        },
      };
    });
    setEndTime(adjustedDate);
  };

  const onCategorySelected = (cat: DelayActivityTypeCategory) => {
    let categoryId: DelayActivityTypeCategory | undefined;
    const prevActivityType = sortedActivityTypes.find(
      x => x.id === prevAppointment?.delayOrActivityTypeId,
    );

    if (prevActivityType) {
      categoryId = allDelayTypeCategories.find(
        x => x.id === prevActivityType.delayActivityTypeCategory,
      );
    }

    setFieldsEdited(prev => {
      return {
        ...prev,
        ...{
          categoryId: categoryId?.id !== cat?.id,
        },
      };
    });
    setCategory(cat);
  };

  const onDelayTypeSelected = (borerActivityType?: BorerActivityType) => {
    setFieldsEdited(prev => {
      return {
        ...prev,
        ...{
          borerActivityType: prevAppointment?.delayOrActivityTypeId !== borerActivityType?.id,
        },
      };
    });
    setActivityType(borerActivityType);
  };

  const onCommentChanged = (val: string) => {
    setFieldsEdited(prev => {
      return {
        ...prev,
        ...{
          comment: prevAppointment?.comment !== val,
        },
      };
    });
    setComment(val);
  };

  const createNewCompletedByField = () => {
    setFieldsEdited(prev => {
      return {
        ...prev,
        ...{
          completedByFields: true,
        },
      };
    });
    const newCompletedByFields = [...completedByFields];
    newCompletedByFields.push({
      value: undefined,
      initialValue: undefined,
      hasEdits: false,
      error: undefined,
    });
    setCompletedByFields(newCompletedByFields);
  };

  const updateSelectedCompletedByField = (index: number, emp: EmployeeDocument | null) => {
    const existingEmpFullName = `${completedByFields[index].initialValue?.get(
      'firstName',
    )} ${completedByFields[index].initialValue?.get('lastName')}`;
    const currentEmpFullName = `${emp?.firstName} ${emp?.lastName}`;

    setFieldsEdited(prev => {
      return {
        ...prev,
        ...{
          emp: existingEmpFullName !== currentEmpFullName,
        },
      };
    });

    const newCompletedByFields = [...completedByFields];
    newCompletedByFields[index].value = emp;
    newCompletedByFields[index].hasEdits = existingEmpFullName !== currentEmpFullName;
    setCompletedByFields(newCompletedByFields);
  };

  const removeCompletedByField = (index: number) => {
    setFieldsEdited(prev => {
      return {
        ...prev,
        ...{
          newCompletedByFields: true,
        },
      };
    });
    const newCompletedByFields = [...completedByFields];
    newCompletedByFields.splice(index, 1);

    setCompletedByFields(newCompletedByFields);
  };

  // Input Validation
  const validateSave = useCallback(async () => {
    setStartErrorText('');
    let validSave = true;
    let startTimeError = '';
    let endTimeError = '';

    if (!startTime) validSave = false;
    if (!endTime) validSave = false;
    if (startTime.isAfter(endTime) || startTime.isSame(endTime)) {
      startTimeError = i18n.t('Start time must be before the end time');
      endTimeError = i18n.t('Start time must be before the end time');
      validSave = false;
    }

    if (endTime.diff(startTime, 'minute') < 5) {
      endTimeError = `${translate('Activity')} must be 5 minutes or greater`;
      validSave = false;
    }

    if (
      dayjs.unix(shiftPicker.shiftStartDateUnix).diff(startTime, 'seconds') > 0 ||
      dayjs.unix(shiftPicker.shiftEndDateUnix).diff(startTime, 'seconds') < 0
    ) {
      startTimeError = `Start time must be within shift hours`;
      validSave = false;
    }
    if (
      dayjs.unix(shiftPicker.shiftStartDateUnix).diff(endTime, 'seconds') > 0 ||
      dayjs.unix(shiftPicker.shiftEndDateUnix).diff(endTime, 'seconds') < 0
    ) {
      endTimeError = `End time must be within shift hours`;
      validSave = false;
    }

    if (!activityType) validSave = false;
    setStartErrorText(startTimeError);
    setEndErrorText(endTimeError);
    setCanSave(validSave && hasEdits);
  }, [
    startTime,
    endTime,
    category,
    activityType,
    shiftPicker.Type,
    shiftPicker.currentBorerShiftId,
    completedByFields,
    hasEdits,
  ]);

  useEffect(() => {
    validateSave();
  }, [validateSave]);

  const onSave = async () => {
    setIsSaving(true);

    let borerActivity: BorerActivityDocument | undefined;

    const selectedActivityEmployeeIds = uniq<string>(
      completedByFields.filter(x => x.value).map(y => y.value?.id || ''),
    );

    try {
      borerActivity = await setBorerActivity(
        comment,
        startTime,
        endTime,
        activityType?.id,
        false,
        prevAppointment,
      );

      if (borerActivity)
        await setEmployeesOnBorerShiftActivity(borerActivity.id, selectedActivityEmployeeIds);
    } catch (error) {
      setIsSaving(false);
      console.log('🚀 ~ file: AddBorerActivitySidePanel.tsx ~ line 365 ~ onSave ~ error', error);
      errorNotification('Shift activity could not be saved. Please try again.');
      return;
    }

    successNotification(
      prevAppointment
        ? i18n.t(`${translate('Shift activity')} updated`)
        : i18n.t(`${translate('Shift activity')} added`),
    );
    onClose();
  };

  if (!open) return null;

  return (
    <>
      <GenericSidePanel
        open={open}
        onClose={onClose}
        title={
          prevAppointment
            ? `Edit ${translate('shift activity')}`
            : `Add ${translate('shift activity')}`
        }
        onOpen={onOpen}
        hasEdits={hasEdits}
        canSave={canSave && !readOnly}
        isSaving={isSaving}
        setUndo={onSetUndo}
        onSave={onSave}
        onCancel={onCancel}
        discardNotificationText={`${translate('Shift activity')} draft discarded`}
      >
        {allDelayTypeCategories.length > 0 && activityTypes.length > 0 && (
          <AddBorerActivityCard
            startTime={startTime}
            onStartTimeChanged={onStartTimeChanged}
            startErrorText={startErrorText}
            endErrorText={endErrorText}
            endTime={endTime}
            onEndTimeChanged={onEndTimeChanged}
            category={category}
            onCategorySelected={onCategorySelected}
            activityType={activityType}
            onActivityTypeSelected={onDelayTypeSelected}
            comment={comment}
            onCommentChanged={onCommentChanged}
            completedBy={activityEmployees}
            completedByFields={completedByFields}
            handleAddCompletedByField={createNewCompletedByField}
            handleUpdateSelectedEmployeeField={updateSelectedCompletedByField}
            onRemoveCompletedByField={removeCompletedByField}
            categories={allDelayTypeCategories}
            activityTypes={activityTypes}
            allEmployees={employees}
            readOnly={readOnly}
          />
        )}
      </GenericSidePanel>
    </>
  );
};

export default observer(AddBorerActivitySidePanel);
